import axios from "axios";
import { arrayToObject } from "@/services/helpers";

// get the store instance
import store from "../store/index";

axios.defaults.baseURL = `/b-api/`;
// axios.defaults.headers.common['X-XSRF-TOKEN'] = token; // this is automatically done by axios.
axios.defaults.headers.common.Accept = "application/json";
axios.defaults.headers.common["X-Requested-With"] = "XMLHttpRequest";

// Add a response interceptor.

// A reload here is only ever a recovery attempt. If the same failure comes straight back,
// reloading again fixes nothing and the tab just spins — which is what a public page calling a
// single protected endpoint used to do, once a second, forever. So each reason gets at most one
// reload per cooldown window. The stamp lives in sessionStorage because it has to survive the
// reload it is guarding; an in-memory flag is wiped by the very reload it would be gating.
// The stale-asset guard in router.js works the same way, with a new release also re-arming it.
const RELOAD_COOLDOWN_MS = 60000;

// sessionStorage throws rather than returning null when storage is blocked (a widget iframe on a
// third-party site with cookies disabled), and a throw in here would take out every error path.
function lastReloadAt(key) {
  try {
    return Number(window.sessionStorage.getItem(key)) || 0;
  } catch (error) {
    return 0;
  }
}

function stampReload(key) {
  try {
    window.sessionStorage.setItem(key, String(Date.now()));
  } catch (error) {
    // No storage means no guard. The 401 path does not rely on it — see the loggedIn check below.
  }
}

// Latched once a reload is scheduled, so every failure that lands while the page is on its way out
// is swallowed instead of consuming the cooldown or surfacing. The polls in the main layout go out
// together, so an expired session answers all of them with a 401 in the same tick; without the latch
// the first one reloads and the rest reject into callers with nothing to catch. Same latch as the
// stale-asset recovery in router.js.
let reloading = false;

// True when a reload is scheduled or already under way, false when it was suppressed and the caller
// should be told about the error instead of having it swallowed.
function reloadOnce(reason, delayMs) {
  if (reloading) return true;

  const key = `api-reload:${reason}`;
  const last = lastReloadAt(key);

  if (last && Date.now() - last < RELOAD_COOLDOWN_MS) return false;

  stampReload(key);
  reloading = true;
  setTimeout(() => {
    window.location.reload(true);
  }, delayMs);

  return true;
}

// The session moved to another company (usually switched in another tab). This tab's pages and
// store still hold the old company's data, so it reloads into the new one.
export function reloadOutOfSync() {
  const scheduled = reloadOnce("browser-out-of-sync", 3000);
  if (scheduled) {
    store.dispatch("ui/setSnackbar", {
      variant: "info",
      title: "Browser out of sync with server.",
      message: "You may have opened a second tab or window. Refreshing the page.",
    });
  }

  return scheduled;
}

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      if (error.response.status === 401) {
        // The session already ended and the reload is scheduled: the store says logged out by now,
        // so without this the rest of the burst would be taken for guest traffic and rejected.
        if (reloading) {
          return new Promise(() => {});
        }

        // Guarded the same way setCompanyHeader is: this module and the store import each other.
        const loggedIn = !!(store && store.state && store.state.auth && store.state.auth.loggedIn);

        // A 401 with no session is the ordinary answer to a guest touching a protected endpoint.
        // There is no session to end and nothing a reload can fix, so reject and let the caller's
        // own catch report it. Swallowing it and reloading is what turned one stray request on a
        // public storefront into an endless refresh.
        if (!loggedIn) {
          return Promise.reject(error);
        }

        // The session really did end. Clear it, then reload once so the app boots at login.
        store.commit("auth/logout", "", { root: true });
        store.dispatch("auth/specificCompanyDataReset", "", { root: true });
        store.commit("companies/reset", "", { root: true });

        if (reloadOnce("unauthenticated", 1000)) {
          return new Promise(() => {}); // the page is going away; no point surfacing an error first
        }

        return Promise.reject(error);
      }

      if (error.response.status === 444) {
        if (reloadOutOfSync()) {
          return new Promise(() => {}); // this essentially cancels whatever the response would have been, so no errors shown
        }

        return Promise.reject(error);
      }

      if (error.response.status === 419) {
        // CSRF ISSUE
        store.dispatch("ui/setSnackbar", {
          variant: "info",
          title: "App Error",
          message: "Page must reload, your previous action was likely not completed",
        });
        reloadOnce("csrf", 4000);
      }
    }

    return Promise.reject(error);
  },
);

function setCompanyHeader() {
  delete axios.defaults.headers.common["CURRENT-COMPANY-ID"];
  if (store && store.state.auth && store.state.auth.user && store.state.auth.user.current_company_id) {
    axios.defaults.headers.common["CURRENT-COMPANY-ID"] = store.state.auth.user.current_company_id;
  }
}

export function get(path, config = {}) {
  setCompanyHeader();
  return axios({
    method: "get",
    url: path,
    ...config,
  });
}

/*
The returned promise should act like this
api.downloadFile(`/providers/psa/pdf`).then((response) => {
  var file = new Blob([response.data], {type: 'application/pdf'});
  var fileURL = URL.createObjectURL(file);

  //To Download the file
  const link = document.createElement('a');
  link.href = fileURL;
  link.setAttribute('download', 'file.pdf');
  document.body.appendChild(link);
  link.click();

  //To Open in new window
  window.open(fileURL);
*/
export function downloadFile(path, data) {
  return axios({
    method: "post",
    url: path,
    responseType: "blob", // important
    data,
  });
}

export function post(path, data) {
  setCompanyHeader();
  return axios({
    method: "post",
    url: path,
    data,
  });
}

export function patch(path, data) {
  setCompanyHeader();
  return axios({
    method: "patch",
    url: path,
    data,
  });
}

export function destroy(path) {
  setCompanyHeader();
  return axios({
    method: "delete",
    url: path,
  });
}

export async function fetchBuyers() {
  setCompanyHeader();
  const response = await axios.get("/buyers");
  return arrayToObject(response.data, "id");
}
